export interface OnShowOptions {
    /** 满足曝光的最小可见像素，默认 1 */
    minPixels?: number;
    /** 首次触发后是否自动停止，默认 true */
    triggerOnce?: boolean;
    /** IO root 与 rootMargin 可选 */
    root?: Element | Document | null;
    rootMargin?: string;
}

export type OnShowParam<T> =
    | T
    | {
        payload?: T;
        options?: OnShowOptions;
    };

function normalizeParam<T>(param?: OnShowParam<T>) {
    if (
        param !== null &&
        typeof param === 'object' &&
        (('options' in (param as any)) || ('payload' in (param as any)))
    ) {
        const p = param as { payload?: T; options?: OnShowOptions };

        return { payload: p.payload as T, options: p.options || {} };
    }

    return { payload: param as T, options: {} as OnShowOptions };
}

/**
 * 高阶函数：创建 Svelte action。
 * - 目标元素在视口可见高度 ≥ minPixels（默认 1）时触发 handler(payload)
 * - 优先使用 IntersectionObserver，不支持时降级为 scroll/resize/orientationchange
 * - 组件卸载/节点移除自动清理；支持 triggerOnce（默认 true）
 */
export function createOnShowAction<T = unknown>(handler: (payload: T) => void, defaultOptions: OnShowOptions = {}) {
    return function action(node: Element, param?: OnShowParam<T>) {
        let { payload, options } = normalizeParam<T>(param);
        const {
            minPixels = 1,
            triggerOnce = true,
            root = null,
            rootMargin = '0px'
        } = { ...defaultOptions, ...options } as OnShowOptions;

        let stopped = false;
        let io: IntersectionObserver | null = null;
        let scrollHandler: (() => void) | null = null;

        const trigger = () => {
            if (stopped) return;
            try {
                handler(payload as T);
            } catch {}

            if (triggerOnce) destroy();
        };

        const checkByPixels = (): boolean => {
            const rect = node.getBoundingClientRect();
            const vw = window.innerWidth || document.documentElement.clientWidth || 0;
            const vh = window.innerHeight || document.documentElement.clientHeight || 0;

            if (vw <= 0 || vh <= 0 || rect.width <= 0 || rect.height <= 0) return false;

            const y1 = Math.max(0, Math.min(vh, rect.top));
            const y2 = Math.max(0, Math.min(vh, rect.bottom));
            const visibleHeight = Math.max(0, y2 - y1);

            return visibleHeight >= minPixels;
        };

        const initIO = () => {
            if (!('IntersectionObserver' in window)) return false;
            io = new IntersectionObserver((entries) => {
                if (!document.contains(node)) {
                    destroy();

                    return;
                }

                for (const entry of entries) {
                    if (!entry.isIntersecting) continue;
                    const rect = entry.intersectionRect;
                    const visibleHeight = Math.max(0, rect.height);

                    if (visibleHeight >= minPixels) {
                        trigger(); break;
                    }
                }
            }, { root: (root as Element) || null, rootMargin, threshold: 0 });
            try {
                io.observe(node);
            } catch {}

            return true;
        };

        const initScroll = () => {
            if (scrollHandler) return;
            scrollHandler = () => {
                if (stopped) return;
                if (!document.contains(node)) {
                    destroy();

                    return;
                }

                if (checkByPixels()) trigger();
            };

            window.addEventListener('scroll', scrollHandler, true);
            window.addEventListener('resize', scrollHandler, true);
            window.addEventListener('orientationchange', scrollHandler, true);
            // 初始检查一次
            scrollHandler();
        };

        const start = () => {
            if (!initIO()) initScroll();
        };

        const destroy = () => {
            stopped = true;
            if (io) {
                try {
                    io.disconnect();
                } catch {}

                io = null;
            }

            if (scrollHandler) {
                window.removeEventListener('scroll', scrollHandler, true);
                window.removeEventListener('resize', scrollHandler, true);
                window.removeEventListener('orientationchange', scrollHandler, true);
                scrollHandler = null;
            }
        };

        start();

        return {
            update(newParam?: OnShowParam<T>) {
                const normalized = normalizeParam<T>(newParam);

                payload = normalized.payload as T;
                const combined = { ...defaultOptions, ...normalized.options } as OnShowOptions;

                // 若核心判定配置改变，简单策略：重建监听

                if (
                    combined.minPixels !== minPixels ||
                    combined.triggerOnce !== triggerOnce ||
                    combined.root !== root ||
                    combined.rootMargin !== rootMargin
                ) {
                    destroy();
                    // 重新赋值局部变量并重启
                    const { minPixels: mp = 1, triggerOnce: to = true, root: r = null, rootMargin: rm = '0px' } = combined;

                    (options as any) = { minPixels: mp, triggerOnce: to, root: r, rootMargin: rm };
                    stopped = false;
                    start();
                } else {
                    // 配置不变，仅更新 payload 即可
                    if (!stopped && io === null && scrollHandler === null) start();
                }
            },
            destroy
        };
    };
}

export default createOnShowAction;
